'use client';

import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import decoration from '@/assets/blog-sticky-banner-desktop-image.png';
import Image from 'next/image';
import { BlogStickyBarConfigurationStoryblok } from '@/types/types-storyblok';

export const PostStickyBar = ({ config }: { config: BlogStickyBarConfigurationStoryblok }) => {
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : false);
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const listener = (e: MediaQueryListEvent) => {
      setIsDesktop(e.matches);
    };
    mediaQuery.addEventListener('change', listener);
    return () => {
      mediaQuery.removeEventListener('change', listener);
    };
  }, []);
  const [expanded, setExpanded] = useState(false);
  const [closing, setClosing] = useState(false);

  const onButtonClick = (e: React.MouseEvent) => {
    if (!expanded && !isDesktop) {
      e.preventDefault();
      setExpanded(true);
      return;
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);

  // on mobile, close the dropdown when interacting outside
  useEffect(() => {
    if (isDesktop || !expanded) return;
    const containerEl = containerRef.current;
    if (!containerEl) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      if (!containerEl.contains(e.target as Node)) {
        setClosing(true);
        setTimeout(() => {
          setClosing(false);
          setExpanded(false);
        }, 1000);
      }
    };
    document.addEventListener('click', handler);
    document.addEventListener('touchmove', handler);
    return () => {
      document.removeEventListener('click', handler);
      document.removeEventListener('touchmove', handler);
    };
  }, [isDesktop, expanded]);

  return (
    <div
      ref={containerRef}
      className="bg-brandGreen-400 text-white sticky top-[calc(var(--header-top-offset,0px)+var(--header-height,0px))] z-20 shadow-1"
    >
      <div
        className={clsx(
          'px-6 py-5 md:px-20 mx-auto max-w-screen-2xl flex lg:flex-row lg:items-center lg:justify-start lg:gap-36 relative z-10',
          expanded ? 'flex-col' : 'flex-row justify-between',
        )}
      >
        <div className="flex flex-col w-max shrink-0">
          <span className="text-xl lg:text-[1.75rem] font-[550]">Prenota la tua visita</span>
          <span className="text-sm lg:text-lg font-medium">Confronta prezzi e tempi</span>
        </div>
        <form
          className={clsx(
            'flex lg:flex-row lg:flex-grow lg:gap-4 lg:justify-start relative',
            expanded || closing ? 'flex-col gap-2' : 'flex-row justify-end',
          )}
          action="/cerca"
        >
          <div
            className={clsx(
              'overflow-hidden flex flex-col lg:flex-row lg:flex-grow gap-2 lg:gap-4 transition-all lg:max-w-xl',
              (!expanded || closing) && 'max-h-0 lg:max-h-max duration-1000',
              !expanded && !closing && 'w-0 lg:w-auto',
              expanded && !closing ? 'max-h-44 lg:max-h-max duration-500' : '',
              // lg:max-h-max w-0 lg:w-auto duration-1000
            )}
          >
            <div className="h-2" />
            <div className="relative flex-grow">
              <div className="absolute flex items-center justify-center h-full left-0 top-0 pl-4">
                <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_7102_1143)">
                    <path
                      d="M20.5 5.78613H16.5V3.78613C16.5 2.68613 15.6 1.78613 14.5 1.78613H10.5C9.4 1.78613 8.5 2.68613 8.5 3.78613V5.78613H4.5C3.4 5.78613 2.5 6.68613 2.5 7.78613V19.7861C2.5 20.8861 3.4 21.7861 4.5 21.7861H20.5C21.6 21.7861 22.5 20.8861 22.5 19.7861V7.78613C22.5 6.68613 21.6 5.78613 20.5 5.78613ZM10.5 3.78613H14.5V5.78613H10.5V3.78613ZM20.5 19.7861H4.5V7.78613H20.5V19.7861Z"
                      fill="#0D9488"
                    />
                    <path d="M13.5 9.78613H11.5V12.7861H8.5V14.7861H11.5V17.7861H13.5V14.7861H16.5V12.7861H13.5V9.78613Z" fill="#0D9488" />
                  </g>
                  <defs>
                    <clipPath id="clip0_7102_1143">
                      <rect width="24" height="24" fill="white" transform="translate(0.5 0.5)" />
                    </clipPath>
                  </defs>
                </svg>
              </div>
              <input
                type="text"
                name="service"
                defaultValue={config.serviceValue}
                placeholder="Cerca"
                className="py-2.5 placeholder:text-neutral-lighter text-neutral-darker pl-14 min-w-52 bg-brandGreen-50 rounded-lg focus:outline-none focus:border-secondary-hover border-transparent border-[2.5px] w-full lg:h-full"
              />
            </div>

            <div className="relative flex-grow">
              <div className="absolute flex items-center justify-center h-full left-0 top-0 pl-4">
                <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M18.157 16.4433L13.914 20.6863C13.7284 20.8721 13.5081 21.0194 13.2656 21.12C13.023 21.2205 12.7631 21.2722 12.5005 21.2722C12.238 21.2722 11.978 21.2205 11.7354 21.12C11.4929 21.0194 11.2726 20.8721 11.087 20.6863L6.843 16.4433C5.72422 15.3245 4.96234 13.899 4.65369 12.3472C4.34504 10.7954 4.50349 9.18684 5.10901 7.72506C5.71452 6.26327 6.7399 5.01387 8.05548 4.13484C9.37107 3.2558 10.9178 2.78662 12.5 2.78662C14.0822 2.78662 15.6289 3.2558 16.9445 4.13484C18.2601 5.01387 19.2855 6.26327 19.891 7.72506C20.4965 9.18684 20.655 10.7954 20.3463 12.3472C20.0377 13.899 19.2758 15.3245 18.157 16.4433V16.4433Z"
                    stroke="#0D9488"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M14.6213 12.9075C15.1839 12.3448 15.5 11.5818 15.5 10.7861C15.5 9.99048 15.1839 9.22742 14.6213 8.66481C14.0587 8.1022 13.2956 7.78613 12.5 7.78613C11.7044 7.78613 10.9413 8.1022 10.3787 8.66481C9.81607 9.22742 9.5 9.99048 9.5 10.7861C9.5 11.5818 9.81607 12.3448 10.3787 12.9075C10.9413 13.4701 11.7044 13.7861 12.5 13.7861C13.2956 13.7861 14.0587 13.4701 14.6213 12.9075Z"
                    stroke="#0D9488"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <input
                type="text"
                name="location"
                placeholder="Es. Milano"
                required
                defaultValue={config.locationValue}
                className="py-2.5 placeholder:text-neutral-lighter text-neutral-darker pl-14 min-w-52  bg-brandGreen-50 rounded-lg focus:outline-none focus:border-secondary-hover border-transparent border-[2.5px] w-full lg:h-full"
              />
            </div>

            {expanded && (
              <button
                type="submit"
                className="bg-primary shadow-lg px-4 py-3 rounded-md text-lg font-semibold h-min"
                onClick={onButtonClick}
              >
                Cerca
              </button>
            )}
          </div>
          <button
            className={clsx(
              ' bg-primary shadow-lg px-4 py-3 rounded-md text-lg font-semibold h-min',
              expanded ? 'opacity-0 absolute pointer-events-none' : 'transition-opacity opacity-100',
            )}
            onClick={onButtonClick}
            type="submit"
          >
            Cerca
          </button>
        </form>
      </div>

      <Image src={decoration} alt="medico" aria-hidden={true} className="absolute z-0 right-0 top-0 h-full w-auto hidden lg:block" />
    </div>
  );
};
